import { injectTags } from '../inject-tags';
import { logMessage } from '../sentry';
import {
  adStateEnum,
  viewModeEnum,
  VPAIDAdUnitV2
} from '../../../@types/vpaid.type';
import {
  AdUnitEventName,
  AdUnitVersion,
  VPAIDViewMode
} from '../../../@types/vpaid.type';
import { templateMacrosEncoded } from '../url-utils';

import { AdUnitEventListener } from './vpaid-ad-unit-event-listener';
import { AdUnitMethodWatcher } from './vpaid-ad-unit-method-watcher';
import { AdUnitStateManager } from './vpaid-ad-unit-state-manager';
import { AdUnitTrackManager } from './vpaid-ad-unit-track-manager';
import VASTClient from '../../../formats/video-sdk3/lib/vast/vast-client';

import { sendMonitoringVideoEvent } from '../TrackAnalyticsHelper';
import { DebugLog } from '../debug-log';
import { getCenterCoordinates } from '../utils';
import { MediaFile } from '../../../formats/video-sdk3/lib/vast/media-file';
import { CreativeVendorRegistry } from '../vendors/creative-vendor.registry';
export interface VpaidOptions {
  adUnitType?: string;
  isTrackingEnabled: boolean;
}

interface EnvironmentVars {
  slot: HTMLElement | null;
  videoSlot: HTMLVideoElement | null;
  videoSlotCanAutoPlay: boolean;
}

interface CreativeData {
  AdParameters: string;
}

let VPAIDCreative: VPAIDAdUnitV2;
let environmentVars: EnvironmentVars;
let creativeData: CreativeData = { AdParameters: '' };
let viewMode = viewModeEnum.NORMAL;
let vast: VASTClient;

const videoContainerId = 'vpaid-main-container';
const videoElementId = 'video';
const defaultMethodWatcherThreshold: number = 2000;
const adLoadedMethodWatcherThreshold: number = 4000;

const adUnitEventListener: AdUnitEventListener = new AdUnitEventListener();
const adUnitStateManager: AdUnitStateManager = new AdUnitStateManager(
  adUnitEventListener
);
const methodWatcher: AdUnitMethodWatcher = new AdUnitMethodWatcher(
  adUnitEventListener
);
const adUnitTrackManager: AdUnitTrackManager = new AdUnitTrackManager(
  adUnitStateManager,
  adUnitEventListener
);

let adLoadedThresholdCallback: (() => void) | undefined;

let vpaidHistory: Record<string, number> = {};

export const init = (
  vastClient: VASTClient,
  mediaFiles: MediaFile[],
  options: VpaidOptions,
  macros: Record<string, string> = {}
): void => {
  try {
    vast = vastClient;
    const vpaidFile = mediaFiles.find(o => o.type === 'application/javascript');
    if (!vpaidFile) {
      console.error('No VPAID file found');
      return;
    }
    let vpaidUrl = vpaidFile.url.trim();

    // Need to replace url encoded macros for moat
    vpaidUrl = templateMacrosEncoded(vpaidUrl || '', macros);

    setVpaidState('calling:injectingVpaidTag');

    injectTags(
      '<script src="' + vpaidUrl + '"></script>',
      '#vpaid-video-container',
      '#vpaid-video-container',
      'body',
      {
        overloadDocumentWrite: false,
        enableSentryLog: false,
        allowInjectIntoBody: true
      }
    );

    let checkGetVPAIDAdInterval = setInterval(() => {
      if (typeof window.getVPAIDAd === 'function') {
        clearInterval(checkGetVPAIDAdInterval);
        initVPAIDAdUnit(options, vastClient);
      }
    }, 50);
  } catch (e) {
    console.error('No VPAID file found');
  }
};

function initVPAIDAdUnit(options: VpaidOptions, vastClient: VASTClient): void {
  VPAIDCreative = window.getVPAIDAd!();
  let handshakedVersion: string = VPAIDCreative.handshakeVersion('2.0');
  const adUnitVersion: AdUnitVersion = deduceVpaidVersion(handshakedVersion);

  adUnitEventListener.initEventListener(adUnitVersion, VPAIDCreative, vast);
  adUnitEventListener.addEventListener('AdStarted', () => {
    const container = document.getElementById(videoContainerId);
    if (container) {
      try {
        VPAIDCreative.resizeAd(container.offsetWidth, container.offsetHeight);
      } catch (error) {
        logMessage('VPAID resize error on AdStarted', undefined, { error });
      }
    }
  });

  methodWatcher.watchAdUnitResponse(VPAIDCreative, {
    threshold: defaultMethodWatcherThreshold,
    adUnitVersion: adUnitVersion,
    thresholdByEventName: {
      AdLoaded: adLoadedMethodWatcherThreshold
    },
    onErrorCallback: (eventName: AdUnitEventName) => {
      sendMonitoringVideoEvent(`vpaid_event_threshold_${eventName}`);
      eventName === 'AdStopped' && window.mraid.close();
      eventName === 'AdLoaded' && onAdLoadedThreshold();
    }
  });

  if (options.isTrackingEnabled) {
    adUnitTrackManager.startTracking();
  }

  environmentVars = {
    slot: document.getElementById(videoContainerId),
    videoSlot: document.getElementById(videoElementId) as HTMLVideoElement,
    videoSlotCanAutoPlay: true
  };
  creativeData.AdParameters = vastClient.getAdParameters();
  viewMode = viewModeEnum.NORMAL;
  initAd();
}

function onAdLoadedThreshold(): void {
  const videoElement = document.getElementById(
    videoElementId
  ) as HTMLVideoElement;
  if (!videoElement) return;

  let hasVideoSrc = videoElement.src !== '';

  if (!hasVideoSrc) {
    videoElement.childNodes.forEach(node => {
      if ((node as HTMLSourceElement).src) {
        hasVideoSrc = true;
      }
    });
  }

  if (hasVideoSrc && videoElement.readyState === 0) {
    adLoadedThresholdCallback && adLoadedThresholdCallback();
  }
}

function initAd(): void {
  setVpaidState('calling:initAd');
  if (environmentVars.slot) {
    VPAIDCreative.initAd(
      environmentVars.slot.offsetWidth,
      environmentVars.slot.offsetHeight,
      viewMode as VPAIDViewMode,
      -1, // bitrate
      creativeData,
      environmentVars
    );
  }
}

export function clickAd(overlayElement: HTMLElement): void {
  const initialPointerEventsValue = overlayElement.style.pointerEvents;
  overlayElement.style.pointerEvents = 'none'; // will allow click to go through the overlay

  try {
    const [x, y] = getCenterCoordinates(document.getElementById('root'));

    const clickableElement = findClickableElement(x, y);
    if (clickableElement) {
      (clickableElement as HTMLElement).click();
    } else {
      DebugLog.log('No clickable element found at the specified coordinates.');
    }
  } catch (error) {
    DebugLog.log('Error when trying to click on ad element:' + error);
  } finally {
    overlayElement.style.pointerEvents = initialPointerEventsValue;
  }
}

export function setAdVolume(volume: number): void {
  try {
    VPAIDCreative && VPAIDCreative.setAdVolume(volume);
  } catch (error) {
    logMessage('VPAID adVolume error', undefined, {
      error
    });
  }
}

export function resizeAd(
  width: number,
  height: number,
  viewMode: VPAIDViewMode
): void {
  try {
    VPAIDCreative && VPAIDCreative.resizeAd(width, height, viewMode);
  } catch (error) {
    logMessage('VPAID resize error', undefined, {
      error
    });
  }
}

export function pauseAd(): void {
  try {
    VPAIDCreative && VPAIDCreative.pauseAd();
  } catch (error) {
    logMessage('VPAID pause error', undefined, {
      error
    });
  }
}

export function stopAd(): void {
  try {
    VPAIDCreative && VPAIDCreative.stopAd();
  } catch (error) {
    logMessage('VPAID stop error', undefined, {
      error
    });
  }
}

export function resumeAd(): void {
  try {
    VPAIDCreative && VPAIDCreative.resumeAd();
  } catch (error) {
    logMessage('VPAID resume error', undefined, {
      error
    });
  }
}

export function expandAd(): void {
  try {
    VPAIDCreative && VPAIDCreative.expandAd();
  } catch (error) {
    logMessage('VPAID expand error', undefined, {
      error
    });
  }
}

export function collapseAd(): void {
  try {
    VPAIDCreative && VPAIDCreative.collapseAd();
  } catch (error) {
    logMessage('VPAID collapse error', undefined, {
      error
    });
  }
}

export function skipAd(): void {
  try {
    VPAIDCreative && VPAIDCreative.skipAd();
  } catch (error) {
    logMessage('VPAID skip error', undefined, {
      error
    });
  }
}

export function startAd(videoPlayer: HTMLVideoElement): void {
  // waiting if the vpaid ad is loaded
  let loadedVPAIDAdInterval = setInterval(() => {
    const state = adUnitStateManager.getState();

    if (CreativeVendorRegistry.handleVpaidStart(videoPlayer)) {
      setVpaidState('calling:startAd');
      VPAIDCreative && VPAIDCreative.startAd();
      clearInterval(loadedVPAIDAdInterval);
    }

    if ([adStateEnum.ERROR, adStateEnum.STARTED].indexOf(state) > -1) {
      clearInterval(loadedVPAIDAdInterval);
    }

    if (state === adStateEnum.LOADED) {
      clearInterval(loadedVPAIDAdInterval);
      try {
        setVpaidState('calling:startAd');

        VPAIDCreative && VPAIDCreative.startAd();
      } catch (error) {
        logMessage('VPAID start error', undefined, {
          error
        });
      }
    }
  }, 50);
}

export function isAdStarted(): boolean {
  return adUnitStateManager.getState() === adStateEnum.STARTED;
}

export function isAdLoaded(): boolean {
  return adUnitStateManager.getState() === adStateEnum.LOADED;
}

export function getVpaidState(): Record<string, number> {
  return vpaidHistory;
}

export function setVpaidState(eventName: string): void {
  if (!vpaidHistory[eventName]) {
    vpaidHistory[eventName] = 1;
  } else {
    vpaidHistory[eventName]++;
  }
}

export function addEventListener(
  eventName: string,
  callback: (...args: any[]) => void
): void {
  if (eventName === 'AdLoadedThreshold') {
    adLoadedThresholdCallback = callback;
    return;
  }
  adUnitEventListener.addEventListener(eventName, callback);
}

export function waitForOngoingTracksToFinish(): Promise<void> {
  return adUnitTrackManager.waitForOngoingTracksToFinish();
}

function deduceVpaidVersion(handshakedVersion: string): AdUnitVersion {
  return parseInt(handshakedVersion, 10) as AdUnitVersion;
}

export function getAdSkippableState(): boolean {
  let result = false;

  try {
    if (VPAIDCreative.getAdSkippableState) {
      result = VPAIDCreative.getAdSkippableState();
    } else if ((VPAIDCreative as any).adSkippableState) {
      result = (VPAIDCreative as any).adSkippableState;
    } else {
      result = false;
    }
  } catch (error) {
    logMessage('VPAID getAdSkippableState error', undefined, {
      error
    });

    result = false;
  }

  return result;
}

function findClickableElement(x: number, y: number): Element | null {
  let targetElement = document.elementFromPoint(x, y);

  while (targetElement && targetElement.tagName.toLowerCase() === 'iframe') {
    try {
      targetElement =
        ((targetElement as unknown) as HTMLIFrameElement).contentDocument?.elementFromPoint(
          x,
          y
        ) || null;
    } catch (error) {
      DebugLog.log('Error when trying to access iframe content:' + error);
      break;
    }
  }

  return targetElement;
}
